import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Button, Alert, AlertDescription, Skeleton } from '@aaas/shared';
import { WidgetForm } from '../components/WidgetForm';
import { useWidgetQuery, useUpdateWidgetMutation } from '../hooks';
import type { WidgetFormData } from '../types/widget.types';

const EditWidgetPage = () => {
  const navigate = useNavigate();
  const { widgetId } = useParams<{ widgetId: string }>();
  const [error, setError] = useState<string>('');

  const {
    data: widget,
    isLoading,
    error: fetchError,
  } = useWidgetQuery({
    widgetId,
  });

  const updateMutation = useUpdateWidgetMutation(widgetId!);

  const handleSubmit = async (data: WidgetFormData) => {
    try {
      setError('');
      await updateMutation.mutateAsync(data);
      // Navigate back to widget detail page
      navigate(`/kms/widgets/${widgetId}`);
    } catch (err) {
      console.error('Failed to update widget:', err);
      setError(err instanceof Error ? err.message : 'Failed to update widget. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate(`/kms/widgets/${widgetId}`);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton variant="rectangular" className="w-10 h-10" />
          <Skeleton variant="text" className="w-64 h-8" />
        </div>
        <div className="space-y-4">
          <Skeleton variant="rectangular" className="w-full h-96" />
        </div>
      </div>
    );
  }

  if (fetchError || !widget) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/kms/widgets')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">Edit Widget</h1>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {fetchError instanceof Error ? fetchError.message : 'Widget not found'}
          </AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/kms/widgets')}>Back to Widgets</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/kms/widgets/${widgetId}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-3xl font-bold">Edit Widget</h1>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <WidgetForm
        initialData={widget}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitLabel="Update Widget"
        isSubmitting={updateMutation.isPending}
      />
    </div>
  );
};

export default EditWidgetPage;
